import { useEffect, useMemo, useState } from 'react';
import { engagementAnalyticsService, EngagementSummary, OptimalPostingTimesResult } from '../services/engagementAnalyticsService';

function dayName(d: number) {
  return ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d] || String(d);
}

export default function EngagementAnalyticsPage() {
  const [summary, setSummary] = useState<EngagementSummary | null>(null);
  const [times, setTimes] = useState<OptimalPostingTimesResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [s, t] = await Promise.all([
          engagementAnalyticsService.getSummary(),
          engagementAnalyticsService.getOptimalTimes(),
        ]);
        if (!mounted) return;
        if (s.success) setSummary(s.data);
        if (t.success) setTimes(t.data);
      } catch (e: any) {
        setError(e?.message || 'Failed to load engagement analytics');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const topCells = useMemo(() => {
    if (!times) return [];
    return times.recommended.slice(0, 6);
  }, [times]);

  if (loading) return <div className="p-6 text-gray-300">Loading engagement analytics…</div>;
  if (error) return <div className="p-6 text-red-400">{error}</div>;

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-semibold text-gray-100">Engagement Analytics</h1>

      {summary && (
        <section className="bg-gray-900/50 rounded-lg p-4 border border-gray-800">
          <h2 className="text-lg font-medium mb-2">Summary</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><div className="text-gray-400">Overall Score</div><div className="text-gray-100 text-xl">{summary.overallScore.toFixed(2)}</div></div>
            <div><div className="text-gray-400">Avg / Post</div><div className="text-gray-100 text-xl">{summary.averagePerPost.toFixed(2)}</div></div>
            <div><div className="text-gray-400">Comments</div><div className="text-gray-100 text-xl">{summary.totalInteractions.comments}</div></div>
            <div><div className="text-gray-400">Shares</div><div className="text-gray-100 text-xl">{summary.totalInteractions.shares}</div></div>
          </div>

          <div className="mt-4">
            <h3 className="text-sm text-gray-300 mb-2">Top Posts</h3>
            <div className="space-y-2">
              {summary.topPosts.slice(0, 5).map(p => (
                <div key={p.postId} className="flex justify-between items-center text-sm bg-gray-800/40 rounded p-2">
                  <div className="truncate mr-4">{p.title || p.postId}</div>
                  <div className="text-gray-300">Score: {p.score.toFixed(2)}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {times && (
        <section className="bg-gray-900/50 rounded-lg p-4 border border-gray-800">
          <h2 className="text-lg font-medium mb-2">Optimal Posting Times</h2>
          <div className="text-xs text-gray-400 mb-3">Data window: last {times.dataWindowDays} days · Events: {times.totalEvents} {times.fallbackUsed ? '(fallback baseline used)' : ''}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-sm text-gray-300 mb-2">Top Slots</h3>
              <div className="space-y-2">
                {topCells.map((c, i) => (
                  <div key={`${c.day}-${c.hour}`} className="flex justify-between items-center text-sm bg-gray-800/40 rounded p-2">
                    <div>{dayName(c.day)} {c.hour}:00</div>
                    <div className="text-gray-300">{c.score.toFixed(2)} · {c.confidence}</div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-sm text-gray-300 mb-2">Heatmap (Preview)</h3>
              <div className="grid grid-cols-8 gap-1">
                <div></div>
                {[...Array(7)].map((_, d) => (
                  <div key={d} className="text-center text-[10px] text-gray-400">{dayName(d)}</div>
                ))}
                {[...Array(24)].map((_, hour) => (
                  <>
                    <div key={`hlabel-${hour}`} className="text-[10px] text-gray-400">{hour}:00</div>
                    {[...Array(7)].map((_, day) => {
                      const cell = times.heatmap.find(h => h.day === day && h.hour === hour);
                      const val = cell?.score || 0;
                      const intensity = Math.min(100, Math.floor(val * 10));
                      return (
                        <div key={`${day}-${hour}`} className="h-5 rounded" style={{ backgroundColor: `hsl(200, 70%, ${Math.max(15, 60 - intensity/2)}%)` }} title={`${dayName(day)} ${hour}:00 · ${val.toFixed(2)}`} />
                      );
                    })}
                  </>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
